/*
 *   File : verify-pan.js
 *   Author : https://evoqins.com
 *   Description : Onboarding step  - Pan verification page
 *   Integrations : react-toastify
 *   Version : 1.0.0
 *   Date : 23-09-2022  
*/

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie"

// *********** Components ***************//  
import { Icon } from "../../../Component/Icon";
import { CustomTextInput, DatePicker, RadioGroup } from "../../../Component/Form-Elements";
import { PrimaryButton } from "../../../Component/Buttons";
import { APIService } from "../../../Service";

// *********** Styles ***************//  
import styles from "../../../Style/Component/login.module.scss";
import { NRIWarningModal } from "../../../Component/Modal";
import { Modal } from "bootstrap";
import Store from "../../../Store/store";
import { _getSignupProgress, _getUserData } from "../../../Helper";
import { format, subYears } from "date-fns";

// Custom data
const CITIZEN_SELECT = [
    {
        id: true,
        display_name: "Yes"
    },
    {
        id: false,
        display_name: "No"
    },
];

function VerifyPan(props) {

    //get progress data form redux
    const PROGRESS_DATA = useSelector((store) => store.Reducer.SIGNUP_PROGRESS_DATA);
    const navigate = useNavigate();

    const [pan, setPan] = useState("");
    const [panError, setPanError] = useState("");
    const [panSuccess, setPanSuccess] = useState("");
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showNRIWarningModal, setShowNRIWarningModal] = useState(false);
    const [isUserNRI, setIsUserNRI] = useState(false);
    const [dob, setDob] = useState("");
    const [dobError, setDobError] = useState("");

    // handling Enter key 
    useEffect(() => {

        const _handleOnEnter = (e) => {
            if (e.key === 'Enter') {
                _handleVerifyPan();
            }
        };

        document.addEventListener('keydown', _handleOnEnter);

        return () => {
            document.removeEventListener('keydown', _handleOnEnter);
        };

    }, [pan, name]);

    useEffect(() => {
        if (PROGRESS_DATA) {
            setPan(PROGRESS_DATA?.kyc_data?.pan || "");
            setName(PROGRESS_DATA?.kyc_data?.name || "");
            setIsUserNRI(PROGRESS_DATA?.kyc_data?.is_nri);
        }
        console.log(PROGRESS_DATA);
    }, [PROGRESS_DATA])


    // Handle open NRI warning modal
    useEffect(() => {
        if (showNRIWarningModal === true) {
            let nri_warning_modal = new Modal(document.getElementById('nri-warning-modal'));
            nri_warning_modal.show();
        };
    }, [showNRIWarningModal]);

    //handle pan change
    const _handlePanChange = (value) => {
        if (value.length <= 10) {
            setPan(value.toUpperCase());
            setPanError("");
        };
    };

    // handle name change   
    const _handleNameChange = (value) => {
        setName(value);
        setNameError("");
    };

    //handle change date
    const _handleDateChange = (date) => {
        setDob(date);
        setDobError("");
    };

    const _handleBackToLogin = () => {
        sessionStorage.removeItem('access_token');
        Cookies.remove('onboarding_refresh_token', { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
        Store.dispatch({
            type: 'UPDATE_ONBOARDING_TOKEN',
            payload:
                { refresh_token: null, access_token: null }
        });
    };

    //handle citizenship select
    const _handleCitizenSelect = (id) => {
        setIsUserNRI(id);
    };

    // handle Verify pan
    const _handleVerifyPan = () => {
        let is_valid = true;
        let validate_pan = /^[A-Z]{3}P[A-Z]{1}\d{4}[A-Z]{1}$/; // validation of PAN

        if (!validate_pan.test(pan)) {
            setPanError("Invalid PAN number");
            is_valid = false;
        }

        if (name.trim().length === 0) {
            setNameError("Name cannot be empty");
            is_valid = false;
        }

        // Validate dob
        if (!dob) {
            setDobError("Date of birth cannot be empty");
            is_valid = false;
        }


        if (is_valid === true) {
            setLoading(true);
            // AP call
            _verifyPAN();
        }
    };

    // ================================ //
    //============= API CALLS ========= //

    //API- Check kyc status
    const _verifyPAN = () => {
        let api_version = 3;
        let is_post = true;
        let url = 'kyc/check-kyc-status';
        let data = {
            "pan": pan,
            "name": name,
            "dob": format(dob, "dd-MM-yyyy"),
            "is_nri": isUserNRI,
            "country_code": PROGRESS_DATA?.kyc_data?.mobile?.country_code,
            "mobile": PROGRESS_DATA?.kyc_data?.mobile?.number
        };
        let token = sessionStorage.getItem('access_token');

        APIService(is_post, url, data, token, api_version).then((response) => {
            if (response.status_code === 200) {


                // Non-KYC NRI user not allowed to invest
                // So we are showing warning modal and preventing KYC completion
                if (response.data.non_kyc_nri) {
                    setShowNRIWarningModal(true);
                    setLoading(false);
                } else {
                    setPanSuccess(true);

                    // Setting toast message based on pan status 
                    toast.dismiss();
                    toast.success("Pan verified successfully", {
                        className: "e-toast",
                    });

                    // For showing success input - border with green color
                    setTimeout(() => {
                        props.handleUpdateCurrentStep(2, response.data.is_kyc_compliant);
                    }, 1000);

                    // To update signup progress data   
                    _getSignupProgress();
                    _getUserData();
                }

            } else {
                toast.dismiss();
                toast.error(response.message, {
                    className: "e-toast",
                });
            }
            setLoading(false);
        });;
    };

    return (
        <div className="col-sm-10 col-md-9 col-lg-8 col-xl-7 mx-auto px-lg-0">
            <div className="e-padding-block-40px-res padding-76px-top padding-150px-bottom position-relative">
                <div className={`${styles.e_onboarding_wrapper_verify_pan} e-border-radius-24`}>
                    <div className="row justify-content-between">
                        <div className="col-md-6 mt-auto pe-0 d-none d-md-block">
                            <img src={require("../../../Assets/Images/Onboarding/pan.webp")}
                                className="img-fluid col-8 ms-4"
                                alt="pan image"
                                draggable="false" />
                        </div>
                        <div className="col-md-6 position-relative gradient-vr-line">
                            <div className="margin-32px-right no-margin-res">
                                <Icon icon="arrow-back-left"
                                    className="cursor-pointer mb-3"
                                    size={24}
                                    onClick={_handleBackToLogin} />
                                <h2 className="text-gradient-primary e-alt-font-poppins e-font-weight-600 e-font-20-res e-font-24 line-height-32px letter-spacing-neg-2per margin-32px-bottom ">
                                    Confirm PAN
                                </h2>

                                <CustomTextInput label="10 Digit PAN*"
                                    placeholder="ANJPL3753F"
                                    maxLength={10}
                                    type="text"
                                    value={pan}
                                    floatingLabel
                                    errorMsg={panError}
                                    successMsg={panSuccess}
                                    icon={panSuccess ? "succesfull" : ""}
                                    className="w-100"
                                    wrapperClassName="mb-4"
                                    handleChange={_handlePanChange} />

                                <CustomTextInput label="Name as per PAN*"
                                    placeholder="Joseph Thomas"
                                    type="text"
                                    floatingLabel
                                    value={name}
                                    errorMsg={nameError}
                                    successMsg={panSuccess}
                                    icon={panSuccess ? "succesfull" : ""}
                                    className="w-100"
                                    handleChange={_handleNameChange} />

                                <div className="mt-4" id='dob'>
                                    <DatePicker value={dob}
                                        label="Date of birth*"
                                        floatingLabel
                                        minDate={subYears(new Date(), 100)}
                                        maxDate={subYears(new Date(), 18)}
                                        error={dobError}
                                        onChange={_handleDateChange} />
                                </div>

                                <div className="mt-3">
                                    <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                        Are you an NRI Investor*
                                    </h6>
                                    <div className='d-flex flex-wrap gap-3'>
                                        <RadioGroup data={CITIZEN_SELECT}
                                            selected={isUserNRI}
                                            handleSelect={_handleCitizenSelect} />
                                    </div>
                                </div>

                                <PrimaryButton name='Confirm PAN'
                                    loading={loading}
                                    disabled={loading}
                                    inActive={pan.length !== 0 && name.length !== 0 && dob.length !== 0 ? false : true}
                                    className={`w-100 padding-12px-tb e-font-16 mt-4`}
                                    onPress={_handleVerifyPan} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {
                showNRIWarningModal &&
                <NRIWarningModal onClose={() => setShowNRIWarningModal(false)} />
            }

        </div>
    )
}

export default VerifyPan;